import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  billingSessionService,
  CreateBillingSessionDto,
  CashDrawerCountDto,
} from "../../services/billing-session.service";
import { counterService, Counter } from "../../services/counter.service";
import { storeService, Store } from "../../services/store.service";
import { useAuthStore } from "../../stores/authStore";
import { useBillingSessionStore } from "../../stores/billingSessionStore";
import { showToast } from "../../lib/toast";

export function OpenBillingSessionPage() {
  const navigate = useNavigate();
  const { selectedStoreId, selectedCounterId, setSelectedCounter } =
    useAuthStore();
  const { setActiveSession, fetchActiveSession } = useBillingSessionStore();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<"session" | "cash-drawer">("session");
  const [stores, setStores] = useState<Store[]>([]);
  const [counters, setCounters] = useState<Counter[]>([]);
  const [formData, setFormData] = useState<CreateBillingSessionDto>({
    storeId: selectedStoreId || "",
    counterId: selectedCounterId || "",
    shift: "MORNING",
    openingCash: 0,
    denominations: [],
  });
  const [cashCount, setCashCount] = useState<CashDrawerCountDto>({
    notes2000: 0,
    notes500: 0,
    notes200: 0,
    notes100: 0,
    notes50: 0,
    notes20: 0,
    notes10: 0,
    coins: 0,
    notes: "",
  });

  const loadCounters = async (storeId: string) => {
    try {
      const response = await counterService.getCounters({
        page: 1,
        limit: 100,
        storeId,
      });
      const list = response.data || [];
      setCounters(list);
      const preferred =
        selectedCounterId && list.some((c) => c.id === selectedCounterId)
          ? selectedCounterId
          : list[0]?.id;
      if (preferred) {
        setFormData((prev) => ({ ...prev, counterId: preferred }));
        setSelectedCounter(preferred);
      }
    } catch (err) {
      setCounters([]);
    }
  };

  const loadStores = async () => {
    try {
      const data = await storeService.getAll();
      setStores(data || []);
    } catch (err) {
      setStores([]);
    }
  };

  // Load stores on mount and load counters if store is already selected
  useEffect(() => {
    loadStores();
    // If there's already a selected store, load its counters
    if (selectedStoreId) {
      loadCounters(selectedStoreId);
    }
  }, []);

  // When selectedStoreId changes (e.g. from header dropdown), update and load counters
  useEffect(() => {
    if (selectedStoreId && formData.storeId !== selectedStoreId) {
      setFormData((prev) => ({ ...prev, storeId: selectedStoreId }));
      loadCounters(selectedStoreId);
    }
  }, [selectedStoreId]);

  useEffect(() => {
    if (selectedCounterId && formData.counterId !== selectedCounterId) {
      setFormData((prev) => ({ ...prev, counterId: selectedCounterId }));
    }
  }, [selectedCounterId]);

  const calculateTotal = () => {
    return (
      (cashCount.notes2000 || 0) * 2000 +
      (cashCount.notes500 || 0) * 500 +
      (cashCount.notes200 || 0) * 200 +
      (cashCount.notes100 || 0) * 100 +
      (cashCount.notes50 || 0) * 50 +
      (cashCount.notes20 || 0) * 20 +
      (cashCount.notes10 || 0) * 10 +
      (cashCount.coins || 0)
    );
  };

  const handleCashCountChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setCashCount((prev) => ({
      ...prev,
      [name]: name === "notes" ? value : parseInt(value) || 0,
    }));
  };

  const handleOpenSession = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.storeId) {
      showToast.error("Please select a store");
      return;
    }

    if (!formData.counterId) {
      showToast.error("Please select a counter");
      return;
    }

    setStep("cash-drawer");
  };

  const handleRecordCashDrawer = async () => {
    try {
      setLoading(true);
      const session = await billingSessionService.openSession({
        ...formData,
        openingCash: calculateTotal(),
        denominations: [
          { denominationPaise: 200_000, count: cashCount.notes2000 ?? 0 },
          { denominationPaise: 50_000, count: cashCount.notes500 ?? 0 },
          { denominationPaise: 20_000, count: cashCount.notes200 ?? 0 },
          { denominationPaise: 10_000, count: cashCount.notes100 ?? 0 },
          { denominationPaise: 5_000, count: cashCount.notes50 ?? 0 },
          { denominationPaise: 2_000, count: cashCount.notes20 ?? 0 },
          { denominationPaise: 1_000, count: cashCount.notes10 ?? 0 },
          { denominationPaise: 100, count: cashCount.coins ?? 0 },
        ],
      });
      setActiveSession(session);
      // Fetch the fully populated session to update header immediately
      if (formData.storeId) {
        await fetchActiveSession(formData.storeId, formData.counterId);
      }
      showToast.success("Billing session started successfully");
      navigate("/billing-sessions");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to open billing session",
      );
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Open Billing Session
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {step === "session"
              ? "Select shift and store"
              : "Count opening cash drawer"}
          </p>
        </div>
        <button
          onClick={() => navigate("/billing-sessions")}
          className="text-gray-600 hover:text-gray-900"
        >
          ← Back
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {step === "session" ? (
          <form onSubmit={handleOpenSession}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shift
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {["MORNING", "AFTERNOON", "EVENING", "NIGHT"].map((shift) => (
                  <button
                    key={shift}
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, shift: shift as any }))
                    }
                    className={`px-4 py-3 rounded-lg border text-center transition ${
                      formData.shift === shift
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-gray-700 border-gray-300 hover:border-blue-500"
                    }`}
                  >
                    {shift}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Store
              </label>
              <select
                value={formData.storeId}
                onChange={(e) => {
                  const storeId = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    storeId,
                    counterId: "",
                  }));
                  if (storeId) {
                    loadCounters(storeId);
                  }
                }}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select store...</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Counter
              </label>
              <select
                value={formData.counterId}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    counterId: e.target.value,
                  }));
                  setSelectedCounter(e.target.value);
                }}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select counter...</option>
                {counters.map((counter) => (
                  <option key={counter.id} value={counter.id}>
                    {counter.name}
                  </option>
                ))}
              </select>
              {counters.length === 0 && formData.storeId && (
                <p className="text-xs text-gray-500 mt-1">
                  No counters found for this store. Create one first.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-4 pt-4 border-t">
              <button
                type="button"
                onClick={() => navigate("/billing-sessions")}
                className="px-6 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? "Opening..." : "Next: Count Cash"}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <h3 className="text-lg font-semibold mb-4">Opening Cash Count</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ₹2000 Notes
                </label>
                <input
                  type="number"
                  name="notes2000"
                  value={cashCount.notes2000}
                  onChange={handleCashCountChange}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ₹500 Notes
                </label>
                <input
                  type="number"
                  name="notes500"
                  value={cashCount.notes500}
                  onChange={handleCashCountChange}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ₹200 Notes
                </label>
                <input
                  type="number"
                  name="notes200"
                  value={cashCount.notes200}
                  onChange={handleCashCountChange}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ₹100 Notes
                </label>
                <input
                  type="number"
                  name="notes100"
                  value={cashCount.notes100}
                  onChange={handleCashCountChange}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ₹50 Notes
                </label>
                <input
                  type="number"
                  name="notes50"
                  value={cashCount.notes50}
                  onChange={handleCashCountChange}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ₹20 Notes
                </label>
                <input
                  type="number"
                  name="notes20"
                  value={cashCount.notes20}
                  onChange={handleCashCountChange}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ₹10 Notes
                </label>
                <input
                  type="number"
                  name="notes10"
                  value={cashCount.notes10}
                  onChange={handleCashCountChange}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coins (₹)
                </label>
                <input
                  type="number"
                  name="coins"
                  value={cashCount.coins}
                  onChange={handleCashCountChange}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">
                  Total Opening Cash:
                </span>
                <span className="text-2xl font-bold text-blue-600">
                  {formatCurrency(calculateTotal())}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                name="notes"
                value={cashCount.notes}
                onChange={handleCashCountChange}
                rows={2}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Any notes about the opening..."
              />
            </div>

            <div className="flex justify-end gap-4 pt-4 mt-6 border-t">
              <button
                type="button"
                onClick={() => setStep("session")}
                className="px-6 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleRecordCashDrawer}
                disabled={loading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? "Starting..." : "Start Session"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OpenBillingSessionPage;
